import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import styled from 'styled-components';
import posthog from 'posthog-js';
import { utils } from '@makeship/core';
import { Caption, H3, H8 } from '../Typography';
import { useStore } from '../../store';
import { isUserLoggedIn } from '../../utils/analytics';
import { parseFeaturedCollectionMetafields, renderDefaultCreatorLogoText } from '../../utils/collections';
import { parseProductMetafieldsFromCollection } from '../../utils/product';

const StyledImage = styled(Image).attrs({
  draggable: false,
})`
  width: 100%;
  object-fit: contain;

  border-radius: 175px;

  user-drag: none;
  -webkit-user-drag: none;
  pointer-events: auto;
`;

const ItemWrapper = styled.div<{ sectionName?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 12px 10px 12px 0;
  min-width: 112px;
  flex: 0 0 auto;

  &:not(:first-child) {
    margin-left: 10px;
  }

  &:first-child {
    margin-left: 16px;
  }

  &:last-child {
    padding-right: 16px;
  }

  ${({ sectionName, theme }) =>
    `
      @media screen and (min-width: ${
        sectionName === 'home_featured_creators' ? theme.breakpoints.tablet : theme.breakpoints.largeTablet
      }px) {
        &:first-child {
          margin-left: 64px;
        }
        &:last-child {
          padding-right: 64px;
        }
      }
    `}
`;

const CreatorImageWrapper = styled.div`
  margin-bottom: 12px;
  height: 112px;
  width: 112px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    height: 175px;
    width: 175px;
  }
`;

const CreatorTitle = styled(H8)`
  color: ${({ theme }) => theme.colors.neutral7};
  font-size: 18px;
  max-width: 112px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 8px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    font-size: 22px;
    max-width: 175px;
  }
`;

const Hyperlink = styled.a``;

const DefaultCreatorLogo = styled.div`
  display: flex;
  border: ${({ theme }) => `1px solid ${theme.colors.defaultCreatorLogoBorder}`};
  background: ${({ theme }) => theme.colors.defaultCreatorLogoBackground};
  object-fit: contain;
  border-radius: 175px;
  height: 112px;
  width: 112px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    height: 175px;
    width: 175px;
  }
`;

const DefaultCreatorLogoText = styled(H3)`
  font-size: 40px;
  margin: auto;
  width: 100%;
`;

const CreatorCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 175px;
`;

const ExtendedViewWrapper = styled.div`
  overflow: visible;
  width: 100vw;
`;

const PeekContainer = styled.div`
  position: relative;
  width: 100vw;
  overflow: visible;
  pointer-events: auto;
`;

const ScrollWrapper = styled.div`
  overflow-x: auto;
  display: flex;
  contain: layout;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  -webkit-overflow-scrolling: touch; // Safari, iOS
  scroll-behavior: smooth; //Safari, iOS
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  cursor: grab;
  user-select: none;
  -webkit-user-drag: none;
`;

type CreatorScrollerProps = {
  creatorCollections: CollectionEdgeResponse[];
  sectionName: string;
  isComingSoon?: boolean;
};

const CreatorScroller: React.FC<CreatorScrollerProps> = ({
  creatorCollections,
  sectionName,
  isComingSoon,
}: CreatorScrollerProps) => {
  const { state } = useStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      // Let mobile scroll natively
      return;
    }
    let isDragging = false;
    let startX = 0;
    let scrollStart = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.pageX - el.offsetLeft;
      scrollStart = el.scrollLeft;
      el.classList.add('dragging');
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault(); // prevent accidental text selection
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 20; // drag speed
      el.scrollLeft = scrollStart - walk;
    };

    const stopDragging = () => {
      isDragging = false;
      el.classList.remove('dragging');
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mouseup', stopDragging);
    el.addEventListener('mouseleave', stopDragging);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('mouseup', stopDragging);
      el.removeEventListener('mouseleave', stopDragging);
    };
  }, []);

  const renderCarouselItems = () =>
    creatorCollections.map((collection) => {
      const { node } = collection;
      const { metafields } = node;
      const { creatorLogoURL, creatorAvatarURL } = parseFeaturedCollectionMetafields(metafields.edges);
      const creatorLogoSrc = creatorLogoURL || creatorAvatarURL;
      const productMetafields = node?.products?.[0]?.node?.metafields?.edges || [];
      const { launch } = parseProductMetafieldsFromCollection(productMetafields);
      const launchDate = utils.getDateLocalCustomFormat(launch || '', 'MMMM D');

      return (
        <ItemWrapper key={node?.id} sectionName={sectionName}>
          <Link href={`/shop/creator/${node?.handle}`} passHref>
            <Hyperlink
              onClick={() => {
                posthog.capture(`featuredCreator_carousel_click`, {
                  creator_handle: node?.handle,
                  creator_title: node?.title,
                  is_logged_in: isUserLoggedIn(state.user),
                  section_name: sectionName,
                });
              }}
            >
              <CreatorCardWrapper>
                <CreatorImageWrapper>
                  {creatorLogoSrc ? (
                    <StyledImage src={creatorLogoSrc} alt={node?.title} width={175} height={175} priority />
                  ) : (
                    <DefaultCreatorLogo>
                      <DefaultCreatorLogoText>{renderDefaultCreatorLogoText(node?.title)}</DefaultCreatorLogoText>
                    </DefaultCreatorLogo>
                  )}
                </CreatorImageWrapper>
                <CreatorTitle>{node?.title || ''}</CreatorTitle>
                {isComingSoon && !!launch && <Caption>🗓️ {launchDate}</Caption>}
              </CreatorCardWrapper>
            </Hyperlink>
          </Link>
        </ItemWrapper>
      );
    });

  return (
    <ExtendedViewWrapper>
      <PeekContainer>
        <ScrollWrapper ref={scrollRef}>{renderCarouselItems()}</ScrollWrapper>
      </PeekContainer>
    </ExtendedViewWrapper>
  );
};

export default CreatorScroller;
